import { Alert, Box, useTheme } from "@mui/material";
import { useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";

import { useAuth } from "@/providers";
import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
  ViewAsOrgButton,
} from "@/shared/components";
import { EntityComments } from "@/shared/components/comments";
import { CreateDatasetButton } from "@/shared/components/CreateDatasetButton";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { useRegisterViewingContext } from "@/shared/services";

import { DatasetHeader } from "./DatasetHeader";
import { DatasetTabs } from "./DatasetTabs";
import type { DatasetContext } from "./useDatasetContext";

/**
 * Dataset detail page: parent layout for the `/datasets/:datasetPath` route
 * tree. Renders the page chrome (breadcrumbs, header, tab bar, comments) and
 * delegates the active tab's body to <Outlet>. Each child tab route reads
 * shared parent-derived values (datasetId, the dataset record, current org)
 * from outlet context via `useDatasetContext` instead of re-deriving them
 * from `useParams`/`useAuth`/`useDataset`.
 *
 * Failure modes:
 * - Missing `:datasetPath` route param: render an inline error and skip the
 *   <Outlet>; no child tab mounts.
 * - `useDataset` errors: rethrown via `throwOnError: true` and propagate up
 *   to the nearest router `errorElement`
 */
export function Dataset() {
  const params = useParams();
  const datasetId = params.datasetPath;
  const theme = useTheme();
  const datasetQuery = useDataset(datasetId, { throwOnError: true });
  const { currentOrganization: org } = useAuth();

  useRegisterViewingContext("dataset", {
    datasetId,
    record: datasetQuery.data,
  });

  // Memoized so the object passed to <Outlet context={...}> keeps a stable
  // reference across renders that don't change any of its inputs. AuthProvider
  // returns a fresh `value` object on every render (see AuthProvider.tsx), so
  // without this memo each parent render would hand child tabs a new context
  // object and invalidate their downstream memos and effects that depend on
  // it by reference.
  const outletContext = useMemo<DatasetContext | null>(() => {
    if (!datasetId) {
      return null;
    }
    return {
      datasetId,
      org,
    };
  }, [datasetId, org]);

  // `outletContext` is null iff `datasetId` is falsy; the second clause is
  // redundant at runtime but narrows `outletContext` to non-null for the
  // <Outlet> below.
  if (!datasetId || !outletContext) {
    return (
      <PageLayout title={`Roboto - Dataset`}>
        <Alert severity="error">Missing dataset id in path</Alert>
      </PageLayout>
    );
  }

  const breadcrumbs = [
    <Link key="list" to="/datasets">
      Datasets
    </Link>,
    <ExpandableCopyText key="datasetId" text={datasetId} trimLength={15} />,
  ];

  return (
    <PageLayout
      title={`Roboto - Dataset - ${datasetId}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
      headerContentRight={
        <>
          <ViewAsOrgButton orgId={datasetQuery.data?.org_id} variant="icon" />
          <CreateDatasetButton />
        </>
      }
    >
      <ErrorBoundary
        fallback={(props) => {
          if (props.error instanceof Error) {
            return <Alert severity="error">{props.error.message}</Alert>;
          }

          return (
            <Alert severity="error">
              Something went wrong. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Alert>
          );
        }}
      >
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          }}
        >
          <DatasetHeader datasetId={datasetId} showTags />
          <DatasetTabs datasetId={datasetId} />
          <Outlet context={outletContext} />
          <Box sx={{ mt: theme.spacing(2) }}>
            <EntityComments entityType={"dataset"} entityId={datasetId} />
          </Box>
        </section>
      </ErrorBoundary>
    </PageLayout>
  );
}
